import React, { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetPromoterByIdQuery, useDeletePromoterMutation, useUpdatePromoterByIdMutation, useGeneratePromoCodeMutation } from '../promoter.service';
import { EditPromoterModal } from './EditPromoterModal';
import { AssignPromoCodeModal } from './AssignPromoCodeModal';
import { PromoCodesSection } from './PromoCodesSection';
import { Spinner } from '../../../components/shared/atoms/spinner';
import { FaArrowLeft, FaEdit, FaTrash, FaEnvelope, FaCalendar, FaUser } from 'react-icons/fa';
// @ts-ignore - sweetalert2 has no types in current setup
import Swal from 'sweetalert2';
import { appendSearchToPath } from 'src/customHooks/usePaginatedTableSearchParams';

export const PromoterDetails: React.FC = () => {
  const { promoterId } = useParams<{ promoterId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const backToPromoters = appendSearchToPath('/promoters', location.search);
  const { data: promoter, isLoading, isError, error, refetch } = useGetPromoterByIdQuery(promoterId || '');
  const [deletePromoter] = useDeletePromoterMutation();
  const [updatePromoterById] = useUpdatePromoterByIdMutation();
  const [generatePromoCode] = useGeneratePromoCodeMutation();
  
  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignPromoCodeModalOpen, setIsAssignPromoCodeModalOpen] = useState(false);
  const promoCodesSectionRef = useRef<{ refetch: () => void }>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (isError || !promoter) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <h2 className="text-2xl font-bold text-red-800 mb-4">Promoter Not Found</h2>
            <p className="text-red-600 mb-6">
              {error && typeof error === 'object' && 'data' in error ? String(error.data) : 'The promoter you are looking for could not be found.'}
            </p>
            <button
              onClick={() => navigate(backToPromoters)}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Back to Promoters
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Enhanced Header */}
          <div className="mb-10">
            <button
              onClick={() => navigate(backToPromoters)}
              className="flex items-center text-emerald-600 hover:text-emerald-700 font-medium mb-6 transition-colors group"
            >
              <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Promoters
            </button>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">{promoter.name}</h1>
                <p className="text-lg text-gray-600">Promoter Details</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 font-medium flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <FaEdit className="w-4 h-4 mr-2" />
                  Edit Promoter
                </button>
                

                
                <button
                  onClick={async () => {
                    const result = await Swal.fire({
                      title: 'Delete Promoter?',
                      html: `
                        <div class="text-left">
                          <p class="mb-2">Are you sure you want to delete <strong>${promoter.name}</strong>? This action cannot be undone.</p>
                          <p class="text-yellow-600">Note: All associated promo codes will be deactivated.</p>
                        </div>
                      `,
                      icon: 'warning',
                      showCancelButton: true,
                      confirmButtonColor: '#ef4444',
                      cancelButtonColor: '#6b7280',
                      confirmButtonText: 'Yes, delete it!',
                      cancelButtonText: 'Cancel',
                      reverseButtons: true
                    });

                    if (result.isConfirmed) {
                      try {
                        // Show loading state
                        Swal.fire({
                          title: 'Deleting...',
                          text: 'Please wait while we delete the promoter and deactivate their promo codes.',
                          allowOutsideClick: false,
                          didOpen: () => {
                            Swal.showLoading();
                          }
                        });

                        await deletePromoter(promoter.id);
                        
                        // Close loading and show success
                        Swal.fire({
                          title: 'Deleted!',
                          text: 'The promoter has been deleted successfully.',
                          icon: 'success',
                          confirmButtonColor: '#10b981'
                        });
                        
                        navigate(backToPromoters);
                      } catch (error) {
                        // Close loading and show error
                        Swal.fire({
                          title: 'Error!',
                          text: 'Failed to delete the promoter. Please try again.',
                          icon: 'error',
                          confirmButtonColor: '#ef4444'
                        });
                      }
                    }
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 font-medium flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <FaTrash className="w-4 h-4 mr-2" />
                  Delete Promoter
                </button>
              </div>
            </div>
          </div>

          {/* Content Sections - Vertical Layout */}
          <div className="space-y-8">
            {/* Account Information */}
            <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-3xl shadow-2xl p-8 border border-gray-100 animate-fade-in-up backdrop-blur-sm">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center animate-fade-in-left">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-600 rounded-2xl mr-4 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group">
                    <FaUser className="w-7 h-7 text-white group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div>
                    <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                      Account Information
                    </span>
                    <div className="h-1 w-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full mt-1"></div>
                  </div>
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <FaUser className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Name</span>
                      <p className="font-semibold text-gray-900">{promoter.name}</p>
                    </div>
                  </div>
                </div>

                {promoter.email && (
                  <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <FaEnvelope className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Email</span>
                        <p className="font-semibold text-gray-900">{promoter.email}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Commission Rate</span>
                      <p className="font-semibold text-gray-900">{promoter.commissionRate}%</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Total Earnings</span>
                      <div className="space-y-1">
                         <p className="font-semibold text-gray-900">
                          {formatCurrency(promoter.totalEarningsInEuros, 'EUR')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <FaCalendar className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Member Since</span>
                      <p className="font-semibold text-gray-900">{formatDate(promoter.createdAt)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300 group/item">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <FaCalendar className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Last Updated</span>
                      <p className="font-semibold text-gray-900">{formatDate(promoter.updatedAt)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Promo Codes Section */}
            <PromoCodesSection 
              ref={promoCodesSectionRef}
              promoterId={promoter.id} 
              onAddNew={() => setIsAssignPromoCodeModalOpen(true)} 
            />
          </div>
        </div>
      </div>

      {/* Modal Components */}
      <EditPromoterModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onConfirm={async (data) => {
          try {
            // Show loading
            Swal.fire({
              title: 'Updating promoter...',
              text: 'Please wait while we update the promoter information.',
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              }
            });

            await updatePromoterById({ promoterId: promoter.id, data }).unwrap();
            
            // Close modal
            setIsEditModalOpen(false);
            
            // Show success
            Swal.fire({
              title: 'Success! 🎉',
              text: 'Promoter has been updated successfully.',
              icon: 'success',
              confirmButtonColor: '#3b82f6'
            });
            
            // Refresh data
            refetch();
          } catch (error) {
            console.error('Error updating promoter:', error);
            Swal.fire({
              title: 'Error! ❌',
              text: 'Failed to update promoter. Please try again.',
              icon: 'error',
              confirmButtonColor: '#ef4444'
            });
          }
        }}
        currentData={{
          name: promoter.name,
          email: promoter.email || '',
          commissionRate: promoter.commissionRate
        }}
      />

      <AssignPromoCodeModal
        isOpen={isAssignPromoCodeModalOpen}
        onClose={() => setIsAssignPromoCodeModalOpen(false)}
        onConfirm={async (data) => {
          try {
            // Show loading
            Swal.fire({
              title: 'Generating promo code...',
              text: 'Please wait while we create your promo code.',
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              }
            });

            const result = await generatePromoCode(data).unwrap();

            // Close modal
            setIsAssignPromoCodeModalOpen(false);

            if (result.success) {
              // Refresh the promo codes list
              if (promoCodesSectionRef.current) {
                promoCodesSectionRef.current.refetch();
              }
              
              Swal.fire({
                title: 'Success! 🎉',
                text: 'Promo code has been generated successfully.',
                icon: 'success',
                confirmButtonColor: '#3b82f6'
              });
            } else {
              Swal.fire({
                title: 'Error! ❌',
                text: result.error || 'Failed to generate promo code.',
                icon: 'error',
                confirmButtonColor: '#ef4444'
              });
            }
          } catch (error) {
            console.error('Error generating promo code:', error);
            Swal.fire({
              title: 'Error! ❌',
              text: 'Failed to generate promo code. Please try again.',
              icon: 'error',
              confirmButtonColor: '#ef4444'
            });
          }
        }}
        promoterId={promoter.id}
      />
    </div>
  );
};
